import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import React, { useState } from "react";
import ImageCropperModal from "./widgets/ImageCropperModal";
import { API_ENDPOINT } from "../utils/constants";
import { getToken } from "../utils/util";
import { toast } from "react-toastify";
import useCompanySettings from "../hooks/useCompanySettings";
import FullImageModal from "./widgets/FullImageModel";

const AccountSetup = () => {
  const { data, isLoading, isError, error, refetch } = useCompanySettings();
  const [showImage, setShowImage] = useState(false);
  const [imageToShow, setImageToShow] = useState("");
  const [cropperOpen, setCropperOpen] = useState(false);
  const [propertyType, setPropertyType] = useState("");
  const [aspectRatio, setAspectRatio] = useState(2 / 1);

  // Handlers for FullImageModal
  const handleCloseImage = () => {
    setShowImage(false);
  };

  const handleShowImage = (imgUrl) => {
    setImageToShow(imgUrl);
    setShowImage(true);
  };

  // Handlers for ImageCropperModal
  const handleOpenCropper = (type, ratio) => {
    setPropertyType(type);
    setAspectRatio(ratio);
    setCropperOpen(true);
  };

  const handleUploadSuccess = (response) => {
    toast.success(response.message);
    refetch(); // Refresh the data after a successful upload
  };

  // Placeholder image if no URL is available
  const placeholderImage = "/no_image.png";

  // Safely access settings properties
  const settings = data?.settings || {};
  const srLogo = settings.srLogo || placeholderImage;
  const bnLogo = settings.bnLogo || placeholderImage;

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" my={2} textAlign={"center"}>
        Update your brand logos
      </Typography>
      {/* Loading State */}
      {isLoading && <Typography>Loading...</Typography>}

      {/* Error State */}
      {isError && (
        <Typography color="error">
          Error: {error?.message || "Something went wrong"}
        </Typography>
      )}

      {/* Main Content */}
      {!isLoading && !isError && (
        <Grid container spacing={4} justifyContent="center">
          {/* srLogo Card (1:1 aspect ratio) */}
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardMedia
                component="img"
                height="200"
                image={srLogo}
                alt="SR Logo"
                sx={{ objectFit: "contain", backgroundColor: "#f5f5f5" }}
              />
              <CardContent>
                <Typography variant="h6" align="center">
                  Square Logo (1:1)
                </Typography>
              </CardContent>
              <CardActions
                sx={{ justifyContent: "space-between", px: 2, pb: 2 }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleShowImage(srLogo)}
                  disabled={srLogo === placeholderImage}
                >
                  View Full
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleOpenCropper("srLogo", 1 / 1)}
                >
                  Change Image
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* bnLogo Card (3:1 aspect ratio) */}
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardMedia
                component="img"
                height="200"
                image={bnLogo}
                alt="BN Logo"
                sx={{ objectFit: "contain", backgroundColor: "#f5f5f5" }}
              />
              <CardContent>
                <Typography variant="h6" align="center">
                  Banner Logo (3:1)
                </Typography>
              </CardContent>
              <CardActions
                sx={{ justifyContent: "space-between", px: 2, pb: 2 }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleShowImage(bnLogo)}
                  disabled={bnLogo === placeholderImage}
                >
                  View Full
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleOpenCropper("bnLogo", 3 / 1)}
                >
                  Change Image
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* FullImageModal for viewing images */}
      <FullImageModal
        imgUrl={imageToShow}
        open={showImage}
        handleClose={handleCloseImage}
      />

      {/* ImageCropperModal for uploading new images */}
      <ImageCropperModal
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        aspectRatio={aspectRatio}
        maxSizeKB={1024}
        uploadUrl={`${API_ENDPOINT}admin/upload-image-settings-main?propertyType=${propertyType}`}
        headers={{
          authorization: `Bearer ${getToken()}`,
        }}
        onSuccess={handleUploadSuccess}
        onError={(error) => console.error("Upload error:", error)}
      />
    </Container>
  );
};

export default AccountSetup;
